import {
  CABINET_TOE_KICK_DEPTH,
  CABINET_TOE_KICK_HEIGHT,
} from '../../../../../../../helpers/tallCabinetInteriorHelper';
import {applyCabinetMaterialUvProjection} from '../modelGeometry/cabinetMaterialUvProjector';

const MINIMUM_GEOMETRY_SIZE = 0.01;

// Prefer the live target dimension and fall back to the feature's authored dimension.
const getPositiveDimension = (targetDimension, initialDimension) => {
  const normalizedTargetDimension = Number(targetDimension);
  if (normalizedTargetDimension > 0) {
    return normalizedTargetDimension;
  }

  const normalizedInitialDimension = Number(initialDimension);
  return normalizedInitialDimension > 0 ? normalizedInitialDimension : null;
};

const arePointsEqual = (pointA, pointB) => (
  Math.abs(pointA.x - pointB.x) < MINIMUM_GEOMETRY_SIZE
  && Math.abs(pointA.y - pointB.y) < MINIMUM_GEOMETRY_SIZE
);

const getPolygonArea = (points) => points.reduce((area, point, pointIndex) => {
  const nextPoint = points[(pointIndex + 1) % points.length];
  return area + point.x * nextPoint.y - nextPoint.x * point.y;
}, 0) / 2;

// Removes invalid/duplicate points and moves the footprint origin to its minimum X/Y corner.
const normalizeContour = (sourcePoints) => {
  if (!Array.isArray(sourcePoints)) {
    return null;
  }

  const finitePoints = sourcePoints
    .map(point => ({x: Number(point && point.x), y: Number(point && point.y)}));
  if (finitePoints.some(point => !Number.isFinite(point.x) || !Number.isFinite(point.y))) {
    return null;
  }

  if (finitePoints.length > 1 && arePointsEqual(finitePoints[0], finitePoints[finitePoints.length - 1])) {
    finitePoints.pop();
  }

  const uniquePoints = finitePoints.filter((point, pointIndex) => (
    pointIndex === 0 || !arePointsEqual(point, finitePoints[pointIndex - 1])
  ));
  if (uniquePoints.length < 3 || Math.abs(getPolygonArea(uniquePoints)) < MINIMUM_GEOMETRY_SIZE) {
    return null;
  }

  const minimumX = Math.min(...uniquePoints.map(point => point.x));
  const minimumY = Math.min(...uniquePoints.map(point => point.y));
  return uniquePoints.map(point => ({
    x: point.x - minimumX,
    y: point.y - minimumY,
  }));
};

// Zero-rotation points keep the generated mesh aligned with the loader's later group rotation.
const getSpacerContour = (data) => normalizeContour(
  data.points_on_zero_rotation && data.points_on_zero_rotation.contour_points
    ? data.points_on_zero_rotation.contour_points
    : data.contour_points
);

// New spacers store this edge explicitly; saved rectangles from older layouts used edge 2.
const getFrontLineIndex = (data, contour) => {
  const frontLineIndex = data.spacer_front_line_index;
  if (Number.isInteger(frontLineIndex) && frontLineIndex >= 0 && frontLineIndex < contour.length) {
    return frontLineIndex;
  }

  // Existing saved rectangular spacers predate the explicit front-edge metadata.
  return contour.length === 4 ? 2 : null;
};

// Moves only the two front-edge vertices inward, leaving the other cabinet edges full depth.
const getRecessedToeKickContour = (contour, frontLineIndex) => {
  const frontPoint = contour[frontLineIndex];
  const nextFrontPointIndex = (frontLineIndex + 1) % contour.length;
  const nextFrontPoint = contour[nextFrontPointIndex];
  const edgeX = nextFrontPoint.x - frontPoint.x;
  const edgeY = nextFrontPoint.y - frontPoint.y;
  const edgeLength = Math.hypot(edgeX, edgeY);
  if (edgeLength < MINIMUM_GEOMETRY_SIZE) {
    return null;
  }

  const center = contour.reduce((result, point) => ({
    x: result.x + point.x / contour.length,
    y: result.y + point.y / contour.length,
  }), {x: 0, y: 0});
  const edgeCenter = {
    x: (frontPoint.x + nextFrontPoint.x) / 2,
    y: (frontPoint.y + nextFrontPoint.y) / 2,
  };
  const firstNormal = {x: -edgeY / edgeLength, y: edgeX / edgeLength};
  const centerDirection = {x: center.x - edgeCenter.x, y: center.y - edgeCenter.y};
  const inwardNormal = firstNormal.x * centerDirection.x + firstNormal.y * centerDirection.y >= 0
    ? firstNormal
    : {x: -firstNormal.x, y: -firstNormal.y};
  const availableDepth = Math.max(...contour.map(point => (
    (point.x - frontPoint.x) * inwardNormal.x + (point.y - frontPoint.y) * inwardNormal.y
  )));
  const recess = Math.min(
    CABINET_TOE_KICK_DEPTH,
    availableDepth - MINIMUM_GEOMETRY_SIZE
  );
  if (recess <= MINIMUM_GEOMETRY_SIZE) {
    return null;
  }

  const recessedContour = contour.map(point => ({...point}));
  [frontLineIndex, nextFrontPointIndex].forEach(pointIndex => {
    recessedContour[pointIndex].x += inwardNormal.x * recess;
    recessedContour[pointIndex].y += inwardNormal.y * recess;
  });

  return Math.abs(getPolygonArea(recessedContour)) >= MINIMUM_GEOMETRY_SIZE
    ? recessedContour
    : null;
};

/**
 * Converts mapped spacer data into pure prism definitions.
 * Wall-mounted spacers receive one full-height body. Floor-mounted spacers receive a full-depth
 * body above a recessed toe-kick. Invalid input returns null so CabinetLoader can use Composer.
 */
export const getSpacerGeometryParts = (data) => {
  const contour = getSpacerContour(data);
  const height = getPositiveDimension(data.target_height, data.height);
  if (!contour || !height || typeof data.is_floor_mounted !== 'boolean') {
    return null;
  }

  if (!data.is_floor_mounted) {
    return {
      body: {contour, height, y: 0},
      toeKick: null,
    };
  }

  const frontLineIndex = getFrontLineIndex(data, contour);
  const toeKickContour = frontLineIndex === null
    ? null
    : getRecessedToeKickContour(contour, frontLineIndex);
  if (!toeKickContour) {
    return null;
  }

  const toeKickHeight = Math.min(CABINET_TOE_KICK_HEIGHT, height);
  const bodyHeight = height - toeKickHeight;
  return {
    body: bodyHeight > 0
      ? {contour, height: bodyHeight, y: toeKickHeight}
      : null,
    toeKick: {
      contour: toeKickContour,
      height: toeKickHeight,
      y: 0,
    },
  };
};

// Extrudes one 2D footprint along height and remaps canvas X/Y into Three.js X/Z.
const createPrismMesh = (part, material, modelScale, name) => {
  if (!part) {
    return null;
  }

  const THREE = window.THREE;
  const shape = new THREE.Shape();
  part.contour.forEach((point, pointIndex) => {
    // Canvas depth grows down; negate it before rotating extrusion Z into Three.js Y.
    const x = point.x * modelScale;
    const y = -point.y * modelScale;
    if (pointIndex === 0) {
      shape.moveTo(x, y);
    } else {
      shape.lineTo(x, y);
    }
  });
  shape.closePath();

  const geometry = new THREE.ExtrudeGeometry(shape, {
    depth: part.height * modelScale,
    bevelEnabled: false,
    steps: 1,
  });
  geometry.rotateX(-Math.PI / 2);
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = name;
  mesh.position.y = part.y * modelScale;
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
};

/** Creates the renderable spacer group and applies the same material UV projection as cabinets. */
export const createSpacerModel = ({data, material, modelScale, shape, parts = getSpacerGeometryParts(data)}) => {
  if (!parts) {
    return null;
  }

  const spacer = new window.THREE.Group();
  spacer.name = 'ProceduralSpacer';

  const body = createPrismMesh(parts.body, material, modelScale, 'SpacerBody');
  const toeKick = createPrismMesh(parts.toeKick, material, modelScale, 'SpacerToeKick');
  if (body) {
    spacer.add(body);
  }
  if (toeKick) {
    spacer.add(toeKick);
  }

  applyCabinetMaterialUvProjection(spacer, shape);
  return spacer;
};
